import type { ReactNode } from "react";
import { Button, FormSection } from "@omnabase/ui";
import type { AdminPush, AdminPushTotals } from "@omnabase/shared";

/** Browser notifications round two (2026-10-18; brief rule 3): the back office's "Device notifications" (titled so since the device
 *  notifications round, 2026-11-01; the rows keep their numbers), read-only numbers from what the push services
 *  answered, plus "Forget" for a switched-off browser (a platform administrator's repair, audited; the server refuses a working browser).
 *  The rows are the kit's facts markup written out, with a space between label and value, so the page's text reads "3 browsers", never
 *  "Browsers3 browsers". */
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function Row({ k, children }: { k: string; children: ReactNode }) {
  return <div className="ob-facts__row"><dt>{k}</dt>{" "}<dd>{children}</dd></div>;
}

/** one workspace's section: members with a browser, the browsers (switched-off ones listed with their reason and Forget), the last 7 days */
export function WorkspaceDeviceNotifications({ push, busy, onForget }: { push: AdminPush; busy: boolean; onForget: (id: string, browser: string) => void }) {
  return (
    <FormSection title="Device notifications" intro="Notifications on members' devices in this workspace, counted from what the push services answered: numbers and device names only.">
      <dl className="ob-facts">
        <Row k="Members">{plural(push.members, "member", "members")} with a browser</Row>
        <Row k="Browsers">{plural(push.browsers, "browser", "browsers")} · {push.disabled.length} switched off</Row>
        <Row k="Last 7 days">{push.sent7d} sent · {push.failed7d} failed</Row>
      </dl>
      {push.disabled.length > 0 && (
        <ul className="ob-push-readout__list" aria-label="Switched-off browsers">
          {push.disabled.map((b) => (
            <li key={b.id} className="ob-push-readout__item">
              <span className="ob-push-readout__name">{b.browser}</span>{" "}
              <span className="ob-push-readout__reason">{b.reason}</span>{" "}
              <Button size="sm" aria-label={`Forget ${b.browser}`} disabled={busy} onClick={() => onForget(b.id, b.browser)}>Forget</Button>
            </li>
          ))}
        </ul>
      )}
    </FormSection>
  );
}

/** the admin home's section: the platform's totals */
export function PlatformDeviceNotifications({ totals }: { totals: AdminPushTotals | null }) {
  if (!totals) return null;
  return (
    <FormSection title="Device notifications" intro="Notifications on devices across every workspace, counted from what the push services answered.">
      <dl className="ob-facts">
        <Row k="Workspaces">{plural(totals.workspaces, "workspace", "workspaces")} with a browser</Row>
        <Row k="Members">{plural(totals.members, "member", "members")} with a browser</Row>
        <Row k="Browsers">{plural(totals.browsers, "browser", "browsers")} · {totals.disabled} switched off</Row>
        <Row k="Last 7 days">{totals.sent7d} sent · {totals.failed7d} failed</Row>
      </dl>
    </FormSection>
  );
}
